// ─────────────────────────────────────────────────────────────────────────────
// Per-unit door data for the fleet-overview and diagnostics prototypes.
//
// Everything here is derived deterministically from the unit's profile in
// data/fleet.ts, so a unit looks the same every time it is selected but
// different from its neighbours.
//
// ANONYMISATION: node addresses use a documentation-style 10.0.x block, not a
// real onboard addressing scheme. Do not reintroduce real values.
// ─────────────────────────────────────────────────────────────────────────────

import { profileFor } from './fleet';
import { DOW_FACTOR, PeriodSelection, dateForIndex, defaultPeriod, indicesFor } from './period';

export const WAGEN = ['A', 'B', 'C', 'D'] as const;
export type Wagen = typeof WAGEN[number];

// ── Display labelling ────────────────────────────────────────────────────────
// Bode/SBB request (J. Funk, 18 Sep): drop the L/R suffix from door positions.
// The suffix identified the platform side and so identified the Stadler
// platform itself. Internal IDs are unchanged — this is a display mapping only.
//
//   A1L → A1   A2L → A2   A1R → A3   A2R → A4   (likewise B, C, D)

const SUFFIX_ORDER = ['1L', '2L', '1R', '2R'];

export function doorLabel(id: string): string {
  const idx = SUFFIX_ORDER.indexOf(id.slice(1));
  return idx >= 0 ? `${id[0]}${idx + 1}` : id;
}

/** L = platform side, R = trackside. Positions 1 (forward) and 2 (rear). */
export const DOOR_IDS: string[] = WAGEN.flatMap(w => [
  `${w}1L`, `${w}2L`, `${w}1R`, `${w}2R`,
]);

function rnd(n: number) { return Math.abs(Math.sin(n * 9301 + 49297) * 233280) % 1; }

// ── Node manifest ────────────────────────────────────────────────────────────

export interface DoorNode {
  id: string;
  label: string;
  ip: string;
  piNr: number;
  wagen: string;
  cycles: number;
  health: number;
}

const BASE_CYCLES: Record<string, number> = {
  A1L: 11934, A1R: 7154, A2L: 8555, A2R: 9234,
  B1L: 9390,  B1R: 8464, B2L: 10546, B2R: 8789,
  C1L: 11971, C1R: 7421, C2L: 6555, C2R: 9234,
  D1L: 10321, D1R: 8112, D2L: 9543, D2R: 7654,
};

const PI_NR: Record<string, number> = {
  A1L: 1,  A1R: 2,  A2L: 3,  A2R: 4,
  B1L: 15, B1R: 16, B2L: 13, B2R: 14,
  C1L: 11, C1R: 12, C2L: 9,  C2R: 10,
  D1L: 5,  D1R: 6,  D2L: 7,  D2R: 8,
};

const WAGEN_SUBNET: Record<string, number> = { A: 10, B: 11, C: 12, D: 13 };

/** Baseline health for a door, before any watch-list override. */
export function doorHealth(unitId: string, doorId: string): number {
  const p = profileFor(unitId);
  if (p.watchDoors[doorId] !== undefined) return p.watchDoors[doorId];

  const seedBase = p.seedOffset + doorId.charCodeAt(0) * 13 + doorId.charCodeAt(2) * 7;
  // High-mileage units drift down a little; misuse-heavy units drift further.
  const wearPenalty = (p.cyclesScale - 1) * 8;
  const misusePenalty = (p.misuseScale - 1) * 4;
  const base = 95 - Math.round(rnd(seedBase) * 10);
  return Math.max(45, Math.min(99, Math.round(base - wearPenalty - misusePenalty)));
}

export function getDoorNodes(unitId: string): DoorNode[] {
  const p = profileFor(unitId);
  return DOOR_IDS.map((id, i) => {
    const wagen = id[0];
    const jitter = 0.9 + rnd(p.seedOffset + i * 31) * 0.2;
    return {
      id,
      // Display label under the new scheme — the raw id keeps the L/R suffix
      // for internal use only. See doorLabel above.
      label: doorLabel(id),
      ip: `10.0.${WAGEN_SUBNET[wagen]}.${170 + (PI_NR[id] % 4)}`,
      piNr: PI_NR[id],
      wagen,
      cycles: Math.round(BASE_CYCLES[id] * p.cyclesScale * jitter),
      health: doorHealth(unitId, id),
    };
  });
}

// ── Fleet-overview usage (prototype 01) ──────────────────────────────────────

export interface OverviewDoor {
  id: string;
  /** Display label under the new scheme, e.g. 'A3'. */
  label: string;
  side: 'A' | 'B';
  pos: 'fwd' | 'rear';
  heat: 1 | 2 | 3 | 4 | 5;
  cycles: number;
  events: number;
}

export interface OverviewCarriage {
  id: string;
  doors: OverviewDoor[];
}

export interface OverviewData {
  carriages: OverviewCarriage[];
  /** Door cycles summed over the selected reporting period. */
  cyclesInPeriod: number;
  /** Number of days the period covers. */
  periodDays: number;
  misuseEvents: number;
  highSeverity: number;
  avgCycleSeconds: number;
  nextServiceDays: number;
  /** Door driving the next service call. */
  worstDoor: string;
  events: Array<{ tone: 'alert' | 'warn' | 'info'; textKey: string; door: string; time: string; place: string }>;
  forecast: Array<{ label: string; pct: number }>;
}

export function getOverview(
  unitId: string,
  period: PeriodSelection = defaultPeriod(),
): OverviewData {
  const p = profileFor(unitId);
  const days = indicesFor(period);

  const carriages: OverviewCarriage[] = WAGEN.map((w, ci) => ({
    id: `C${ci + 1}`,
    doors: (['1L', '2L', '1R', '2R'] as const).map((suffix, di) => {
      const doorId = `${w}${suffix}`;
      const platform = suffix.endsWith('L');
      const s = p.seedOffset + ci * 41 + di * 17;

      // Platform doors carry most of the traffic.
      const raw = (platform ? 0.55 : 0.2) + rnd(s) * 0.45;
      const scaled = Math.min(1, raw * p.loadScale);
      const heat = Math.max(1, Math.min(5, Math.ceil(scaled * 5))) as 1 | 2 | 3 | 4 | 5;

      // Per-day rate, then summed across the selected period with the
      // weekday profile applied, so a week and a month carry their own mix of
      // commuter peaks and quiet weekends rather than a flat daily average.
      const perDay = (120 + scaled * 520) * (0.85 + rnd(s + 3) * 0.3);
      const cycles = Math.round(days.reduce(
        (acc, dayIdx) => acc
          + perDay
          * DOW_FACTOR[dateForIndex(dayIdx).getDay()]
          * (0.92 + rnd(s + dayIdx * 13) * 0.16),
        0,
      ));

      const eventChance = scaled * 0.35 * p.misuseScale;
      const events = rnd(s + 7) < eventChance ? Math.ceil(rnd(s + 11) * 3 * p.misuseScale) : 0;

      return {
        id: `C${ci + 1}-${platform ? 'A' : 'B'}${suffix[0]}`,
        label: doorLabel(doorId),
        side: platform ? 'A' : 'B',
        pos: suffix[0] === '1' ? 'fwd' : 'rear',
        heat,
        cycles,
        events,
      } as OverviewDoor;
    }),
  }));

  const allDoors = carriages.flatMap(c => c.doors);
  const cyclesInPeriod = allDoors.reduce((a, d) => a + d.cycles, 0);
  const misuseEvents = allDoors.reduce((a, d) => a + d.events, 0);
  const highSeverity = allDoors.filter(d => d.events >= 2).length;

  // Health ordering drives both the service forecast and the worst-door call.
  const ranked = DOOR_IDS
    .map(id => ({ id, health: doorHealth(unitId, id) }))
    .sort((a, b) => a.health - b.health);

  const worst = ranked[0];
  const nextServiceDays = Math.max(3, Math.round((worst.health - 40) * 0.9));

  const forecast = ranked.slice(0, 4).map(r => ({
    label: doorLabel(r.id),
    pct: Math.max(20, Math.min(95, Math.round(100 - r.health + 30))),
  }));
  forecast.push({ label: 'others', pct: 18 });

  // Event feed: pick the noisiest doors so the list matches the heat map.
  const noisy = [...allDoors].sort((a, b) => b.events - a.events || b.cycles - a.cycles);
  const times = ['14:28', '13:55', '12:11', '09:40'];
  const tones: Array<'alert' | 'warn' | 'info'> = ['alert', 'warn', 'warn', 'info'];
  const textKeys = ['event.forcedEntry', 'event.slowClose', 'event.forcedEntry', 'event.cycleMilestone'];

  const events = noisy.slice(0, 4).map((d, i) => ({
    tone: tones[i],
    textKey: textKeys[i],
    door: d.label,
    time: times[i],
    place: `${d.side === 'A' ? 'platform' : 'trackside'} · ${d.pos}`,
  }));

  return {
    carriages,
    cyclesInPeriod,
    periodDays: days.length,
    misuseEvents,
    highSeverity,
    avgCycleSeconds: Math.round((3.8 + (p.cyclesScale - 1) * 1.2 + rnd(p.seedOffset) * 0.6) * 10) / 10,
    nextServiceDays,
    worstDoor: doorLabel(worst.id),
    events,
    forecast,
  };
}
